import {
  KakaoButton, KakaoEmphasizeType, KakaoMessageType, KakaoQuickReply,
  KakaoTemplate, KakaoTemplateBody,
} from '../api/kakaoTemplate';
import { extractSmsTemplateVariables, renderSmsTemplatePreview } from './smsTemplateVariables';

export interface KakaoDraft {
  targetKind: 'channel' | 'group';
  targetId: string;
  name: string;
  content: string;
  categoryCode: string;
  messageType: KakaoMessageType;
  emphasizeType: KakaoEmphasizeType;
  extra: string;
  ad: string;
  emphasizeTitle: string;
  emphasizeSubtitle: string;
  imageId: string;
  header: string;
  highlightTitle: string;
  highlightDescription: string;
  highlightImageId: string;
  items: Array<{ title: string; description: string }>;
  summaryTitle: string;
  summaryDescription: string;
  buttons: KakaoButton[];
  quickReplies: KakaoQuickReply[];
  securityFlag: boolean;
}

export type KakaoDraftErrors = Partial<Record<keyof KakaoDraft | 'item', string>>;

export const createKakaoDraft = (): KakaoDraft => ({
  targetKind: 'channel', targetId: '', name: '', content: '', categoryCode: '',
  messageType: 'BA', emphasizeType: 'NONE', extra: '', ad: '',
  emphasizeTitle: '', emphasizeSubtitle: '', imageId: '', header: '',
  highlightTitle: '', highlightDescription: '', highlightImageId: '',
  items: [], summaryTitle: '', summaryDescription: '', buttons: [], quickReplies: [], securityFlag: false,
});

export const kakaoTemplateToDraft = (template: KakaoTemplate): KakaoDraft => ({
  ...createKakaoDraft(),
  targetKind: template.channelGroupId ? 'group' : 'channel',
  targetId: template.channelGroupId || template.channelId || '',
  name: template.name || '', content: template.content || '', categoryCode: template.categoryCode || '',
  messageType: template.messageType || 'BA', emphasizeType: template.emphasizeType || 'NONE',
  extra: template.extra || '', ad: template.ad || '',
  emphasizeTitle: template.emphasizeTitle || '', emphasizeSubtitle: template.emphasizeSubtitle || '',
  imageId: template.imageId || '', header: template.header || '',
  highlightTitle: template.highlight?.title || '', highlightDescription: template.highlight?.description || '',
  highlightImageId: template.highlight?.imageId || '',
  items: (template.item?.list || []).map(item => ({ title: item.title || '', description: item.description || '' })),
  summaryTitle: template.item?.summary?.title || '', summaryDescription: template.item?.summary?.description || '',
  buttons: template.buttons || [], quickReplies: template.quickReplies || [], securityFlag: template.securityFlag || false,
});

export const validateKakaoDraft = (draft: KakaoDraft): KakaoDraftErrors => {
  const errors: KakaoDraftErrors = {};
  if (!draft.targetId.trim()) errors.targetId = '카카오 채널 또는 채널 그룹을 선택해 주세요.';
  if (!draft.name.trim()) errors.name = '템플릿 이름을 입력해 주세요.';
  if (!draft.categoryCode) errors.categoryCode = '카테고리를 선택해 주세요.';
  if (!draft.content.trim()) errors.content = '알림톡 내용을 입력해 주세요.';
  else if (draft.content.length > 1000) errors.content = '본문은 1,000자 이내로 입력해 주세요.';

  if (draft.messageType === 'EX' || draft.messageType === 'MI') {
    if (!draft.extra.trim()) errors.extra = '부가정보를 입력해 주세요.';
    else if (draft.extra.length > 80 || extractSmsTemplateVariables(draft.extra).length) errors.extra = '부가정보는 변수 없이 80자 이내로 입력해 주세요.';
  }
  if ((draft.messageType === 'AD' || draft.messageType === 'MI') && draft.buttons[0]?.buttonType !== 'AC') {
    errors.buttons = '첫 번째 버튼을 채널 추가로 설정해 주세요.';
  }
  if (draft.buttons.some((button, index) => button.buttonType === 'AC' && (index !== 0 || !['AD', 'MI'].includes(draft.messageType)))) {
    errors.buttons = '채널 추가 버튼은 채널추가형 또는 복합형의 첫 번째 버튼에만 사용할 수 있습니다.';
  }
  if ((draft.messageType === 'AD' || draft.messageType === 'MI') && draft.ad.length > 80) errors.ad = '광고 문구는 80자 이내로 입력해 주세요.';

  if (draft.emphasizeType === 'TEXT') {
    if (!draft.emphasizeTitle.trim()) errors.emphasizeTitle = '강조 제목을 입력해 주세요.';
    if (!draft.emphasizeSubtitle.trim()) errors.emphasizeSubtitle = '강조 보조 문구를 입력해 주세요.';
    if (draft.emphasizeTitle.length > 50) errors.emphasizeTitle = '강조 제목은 50자 이내로 입력해 주세요.';
    if (draft.emphasizeSubtitle.length > 50) errors.emphasizeSubtitle = '강조 보조 문구는 50자 이내로 입력해 주세요.';
  }
  if (draft.emphasizeType === 'IMAGE' && !draft.imageId) errors.imageId = '템플릿 이미지를 선택해 주세요.';
  if (draft.emphasizeType === 'ITEM_LIST') {
    const hasHighlight = Boolean(draft.highlightTitle || draft.highlightDescription || draft.highlightImageId);
    if (!draft.header && !hasHighlight && !draft.items.length && !draft.imageId) errors.item = '헤더, 하이라이트, 아이템 또는 이미지 중 하나를 입력해 주세요.';
    if (draft.header.length > 16) errors.header = '헤더는 16자 이내로 입력해 주세요.';
    if (hasHighlight && (!draft.highlightTitle.trim() || !draft.highlightDescription.trim())) errors.item = '하이라이트 제목과 설명을 함께 입력해 주세요.';
    if (draft.highlightTitle.length > 30 || draft.highlightDescription.length > 16) errors.item = '하이라이트 제목은 30자, 설명은 16자 이내로 입력해 주세요.';
    if (draft.items.length === 1 || draft.items.length > 10 || draft.items.some(item => !item.title.trim() || !item.description.trim() || item.title.length > 6 || item.description.length > 23)) {
      errors.item = '아이템은 2~10개, 제목 6자·내용 23자 이내로 입력해 주세요.';
    }
    if (Boolean(draft.summaryTitle) !== Boolean(draft.summaryDescription)) errors.item = '합계 제목과 내용을 함께 입력해 주세요.';
    if (draft.summaryTitle.length > 6 || draft.summaryDescription.length > 14) errors.item = '합계 제목은 6자, 내용은 14자 이내로 입력해 주세요.';
  }

  const buttonLimit = draft.quickReplies.length ? 2 : 5;
  if (draft.buttons.length > buttonLimit) errors.buttons = `버튼은 최대 ${buttonLimit}개까지 입력할 수 있습니다.`;
  if (draft.buttons.some(button => !button.buttonName.trim() || extractSmsTemplateVariables(button.buttonName).length || (button.buttonType === 'WL' && !button.linkMo?.trim()) || (button.buttonType === 'AL' && (!button.linkAnd?.trim() || !button.linkIos?.trim())))) {
    errors.buttons = '버튼 이름과 유형에 필요한 링크를 입력해 주세요.';
  }
  if (draft.quickReplies.length > 10 || draft.quickReplies.some(reply => !reply.name.trim() || extractSmsTemplateVariables(reply.name).length || (reply.linkType === 'WL' && !reply.linkMo?.trim()) || (reply.linkType === 'AL' && (!reply.linkAnd?.trim() || !reply.linkIos?.trim())))) {
    errors.quickReplies = '바로연결은 10개 이내로, 이름과 필요한 링크를 입력해 주세요.';
  }
  return errors;
};

export const kakaoDraftToBody = (draft: KakaoDraft): KakaoTemplateBody => {
  const buttons = draft.buttons.map(button => ({
    buttonType: button.buttonType, buttonName: button.buttonName,
    ...(button.buttonType === 'WL' && { linkMo: button.linkMo, ...(button.linkPc && { linkPc: button.linkPc }), ...(button.targetOut !== undefined && { targetOut: button.targetOut }) }),
    ...(button.buttonType === 'AL' && { linkAnd: button.linkAnd, linkIos: button.linkIos }),
    ...(['BK', 'MD', 'BC', 'BT'].includes(button.buttonType) && button.chatExtra && { chatExtra: button.chatExtra }),
  }));
  const quickReplies = draft.quickReplies.map(reply => ({
    linkType: reply.linkType, name: reply.name,
    ...(reply.linkType === 'WL' && { linkMo: reply.linkMo, ...(reply.linkPc && { linkPc: reply.linkPc }), ...(reply.targetOut !== undefined && { targetOut: reply.targetOut }) }),
    ...(reply.linkType === 'AL' && { linkAnd: reply.linkAnd, linkIos: reply.linkIos }),
    ...(['BK', 'MD', 'BC', 'BT'].includes(reply.linkType) && reply.chatExtra && { chatExtra: reply.chatExtra }),
  }));
  const body: KakaoTemplateBody = {
    ...(draft.targetKind === 'channel' ? { channelId: draft.targetId.trim() } : { channelGroupId: draft.targetId.trim() }),
    name: draft.name.trim(), content: draft.content, categoryCode: draft.categoryCode,
    messageType: draft.messageType, emphasizeType: draft.emphasizeType,
    buttons, quickReplies, securityFlag: draft.securityFlag,
  };
  if (draft.messageType === 'EX' || draft.messageType === 'MI') body.extra = draft.extra.trim();
  if ((draft.messageType === 'AD' || draft.messageType === 'MI') && draft.ad.trim()) body.ad = draft.ad.trim();
  if (draft.emphasizeType === 'TEXT') {
    body.emphasizeTitle = draft.emphasizeTitle.trim();
    body.emphasizeSubtitle = draft.emphasizeSubtitle.trim();
  }
  if ((draft.emphasizeType === 'IMAGE' || draft.emphasizeType === 'ITEM_LIST') && draft.imageId) body.imageId = draft.imageId;
  if (draft.emphasizeType === 'ITEM_LIST') {
    if (draft.header) body.header = draft.header;
    if (draft.highlightTitle || draft.highlightDescription || draft.highlightImageId) body.highlight = {
      title: draft.highlightTitle, description: draft.highlightDescription,
      ...(draft.highlightImageId && { imageId: draft.highlightImageId }),
    };
    if (draft.items.length || draft.summaryTitle || draft.summaryDescription) body.item = {
      ...(draft.items.length && { list: draft.items }),
      ...((draft.summaryTitle || draft.summaryDescription) && { summary: { title: draft.summaryTitle, description: draft.summaryDescription } }),
    };
  }
  return body;
};

export const buildKakaoPatchChanges = (original: KakaoDraft, next: KakaoDraft): Record<string, unknown> => {
  const before = kakaoDraftToBody(original);
  const after = kakaoDraftToBody(next);
  const changes: Record<string, unknown> = {};
  const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)])) as Array<keyof KakaoTemplateBody>;
  for (const key of keys) {
    if (key === 'channelId' || key === 'channelGroupId') continue;
    if (JSON.stringify(before[key]) === JSON.stringify(after[key])) continue;
    changes[key] = key in after ? after[key] : key === 'buttons' || key === 'quickReplies' ? [] : null;
  }
  return changes;
};

export const getKakaoDraftVariables = (draft: KakaoDraft): string[] => {
  const body = kakaoDraftToBody(draft);
  const parts = [body.content, body.emphasizeTitle || '', body.emphasizeSubtitle || '', body.header || '',
    body.highlight?.title || '', body.highlight?.description || '',
    ...(body.item?.list || []).map(item => item.description || ''), body.item?.summary?.description || '',
    ...(body.buttons || []).flatMap(button => [button.linkMo || '', button.linkPc || '', button.linkAnd || '', button.linkIos || '']),
    ...(body.quickReplies || []).flatMap(reply => [reply.linkMo || '', reply.linkPc || '', reply.linkAnd || '', reply.linkIos || ''])];
  return extractSmsTemplateVariables(parts.join('\n'));
};

export const renderKakaoDraftText = (value: string, variables: Record<string, string>) => renderSmsTemplatePreview(value, variables);
